import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Plus, Pencil, Trash2, Search, DollarSign,
  Calendar, Users, AlertCircle, CheckCircle2, Clock,
  Receipt, TrendingUp, Percent, FileText, ChevronDown, ChevronUp,
  UserPlus, LayoutGrid, List, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FinanzasModule({ clubId, canEdit, categorias = [], integrantes = [], section = 'resumen', viewMode = 'cards', onToggleViewMode, onRefresh, refreshing = false }) {
  const [loading, setLoading] = useState(true);
  const [searchPlan, setSearchPlan] = useState('');
  
  // Data states
  const [resumen, setResumen] = useState(null);
  const [planes, setPlanes] = useState([]);
  const [pagos, setPagos] = useState([]);
  
  // Filtros para resumen financiero
  const currentYear = new Date().getFullYear();
  const [filtros, setFiltros] = useState({
    fecha_desde: `${currentYear}-01-01`,
    fecha_hasta: new Date().toISOString().split('T')[0],
    categoria_id: 'todos',
    tipo_ingreso: 'todos'
  });
  
  // Collapsible states for dashboard
  const [openSections, setOpenSections] = useState({
    saldoCaja: true,
    saldoPeriodo: true,
    morosidades: true,
    ingresos: true,
    egresos: true,
    graficos: true
  });
  
  // Dialog states
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showPagoDialog, setShowPagoDialog] = useState(false);
  const [showCargoDialog, setShowCargoDialog] = useState(false);
  const [showExcepcionDialog, setShowExcepcionDialog] = useState(false);
  const [showAsignarPlanDialog, setShowAsignarPlanDialog] = useState(false);
  const [showAsignarMasivoDialog, setShowAsignarMasivoDialog] = useState(false);
  
  // Form states
  const [planForm, setPlanForm] = useState({
    nombre: '', monto: '', frecuencia: 'mensual', numero_cuotas: 12, dia_vencimiento: 5, descripcion: ''
  });
  const [pagoForm, setPagoForm] = useState({
    integrante_id: '', cuota_ids: [], monto: '', fecha_pago: new Date().toISOString().split('T')[0],
    glosa: '', metodo_pago: 'efectivo', comprobante: ''
  });
  const [cargoForm, setCargoForm] = useState({
    integrante_id: '', monto: '', descripcion: '', fecha_vencimiento: ''
  });
  const [excepcionForm, setExcepcionForm] = useState({
    integrante_id: '', tipo: 'descuento_permanente', valor: '', meses_exencion: '', motivo: ''
  });
  const [asignarForm, setAsignarForm] = useState({
    integrante_id: '', plan_id: '', fecha_inicio: new Date().toISOString().split('T')[0], cuota_inicio: 1
  });
  const [asignarMasivoForm, setAsignarMasivoForm] = useState({
    modo: 'categoria',
    categoria_id: '',
    integrantes_ids: [],
    plan_id: '',
    fecha_inicio: new Date().toISOString().split('T')[0],
    cuota_inicio: 1
  });
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIntegrante, setSelectedIntegrante] = useState(null);
  const [integranteCuotas, setIntegranteCuotas] = useState(null);
  
  // Search state for mass assignment manual selection
  const [massSearchQuery, setMassSearchQuery] = useState('');
  
  const [editingPlan, setEditingPlan] = useState(null);

  // Get approved integrantes
  const integrantesAprobados = integrantes.filter(i => 
    i.estado_inscripcion === 'aprobado' || !i.estado_inscripcion || i.estado === 'activo'
  );

  // Local search for integrantes (used by IntegranteSelector)
  const searchIntegrantesLocal = (query) => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    return integrantesAprobados.filter(int => 
      int.nombre?.toLowerCase().includes(q) ||
      int.apellido?.toLowerCase().includes(q) ||
      int.rut?.toLowerCase().includes(q)
    ).slice(0, 20);
  };

  // Filter integrantes for mass selection (maintains selection state)
  const filteredIntegrantesForMass = massSearchQuery.length >= 2
    ? integrantesAprobados.filter(int => 
        int.nombre?.toLowerCase().includes(massSearchQuery.toLowerCase()) ||
        int.apellido?.toLowerCase().includes(massSearchQuery.toLowerCase()) ||
        int.rut?.toLowerCase().includes(massSearchQuery.toLowerCase())
      )
    : integrantesAprobados;

  useEffect(() => {
    if (clubId) {
      loadData();
    }
  }, [clubId]);

  useEffect(() => {
    if (clubId && section === 'resumen') {
      loadResumen();
    }
  }, [filtros, clubId, section]);

  const loadResumen = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.fecha_desde) params.append('fecha_desde', filtros.fecha_desde);
      if (filtros.fecha_hasta) params.append('fecha_hasta', filtros.fecha_hasta);
      if (filtros.categoria_id && filtros.categoria_id !== 'todos') params.append('categoria_id', filtros.categoria_id);
      if (filtros.tipo_ingreso && filtros.tipo_ingreso !== 'todos') params.append('tipo_ingreso', filtros.tipo_ingreso);
      
      const res = await axios.get(`${API}/clubes-deportivos/${clubId}/resumen-financiero?${params.toString()}`, { withCredentials: true });
      setResumen(res.data);
    } catch (error) {
      console.error('Error loading resumen:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [resumenRes, planesRes, pagosRes] = await Promise.all([
        axios.get(`${API}/clubes-deportivos/${clubId}/resumen-financiero`, { withCredentials: true }),
        axios.get(`${API}/clubes-deportivos/${clubId}/planes-cuotas`, { withCredentials: true }),
        axios.get(`${API}/clubes-deportivos/${clubId}/pagos`, { withCredentials: true })
      ]);
      setResumen(resumenRes.data);
      setPlanes(planesRes.data);
      setPagos(pagosRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load integrante cuotas
  const loadIntegranteCuotas = async (integranteId) => {
    try {
      const res = await axios.get(`${API}/clubes-deportivos/${clubId}/integrantes/${integranteId}/cuotas`, { withCredentials: true });
      setIntegranteCuotas(res.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar cuotas del integrante');
    }
  };

  // Plan CRUD
  const handleSavePlan = async () => {
    try {
      if (editingPlan) {
        await axios.put(`${API}/clubes-deportivos/${clubId}/planes-cuotas/${editingPlan.id}`, planForm, { withCredentials: true });
        toast.success('Plan actualizado');
      } else {
        await axios.post(`${API}/clubes-deportivos/${clubId}/planes-cuotas`, {
          ...planForm,
          monto: parseFloat(planForm.monto),
          numero_cuotas: parseInt(planForm.numero_cuotas),
          dia_vencimiento: parseInt(planForm.dia_vencimiento)
        }, { withCredentials: true });
        toast.success('Plan creado exitosamente');
      }
      setShowPlanDialog(false);
      setEditingPlan(null);
      setPlanForm({ nombre: '', monto: '', frecuencia: 'mensual', numero_cuotas: 12, dia_vencimiento: 5, descripcion: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('¿Eliminar este plan?')) return;
    try {
      await axios.delete(`${API}/clubes-deportivos/${clubId}/planes-cuotas/${planId}`, { withCredentials: true });
      toast.success('Plan eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar plan');
    }
  };

  // Asignar plan individual
  const handleAsignarPlan = async () => {
    try {
      const res = await axios.post(`${API}/clubes-deportivos/${clubId}/asignar-plan`, asignarForm, { withCredentials: true });
      toast.success(res.data.message);
      setShowAsignarPlanDialog(false);
      setAsignarForm({ integrante_id: '', plan_id: '', fecha_inicio: new Date().toISOString().split('T')[0], cuota_inicio: 1 });
      setSelectedIntegrante(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar plan');
    }
  };

  // Asignar plan masivo
  const handleAsignarMasivo = async () => {
    let integrantesAAsignar = [];
    
    if (asignarMasivoForm.modo === 'todos') {
      integrantesAAsignar = integrantesAprobados.map(i => i.id);
    } else if (asignarMasivoForm.modo === 'categoria') {
      integrantesAAsignar = integrantesAprobados
        .filter(i => i.categoria_ids?.includes(asignarMasivoForm.categoria_id))
        .map(i => i.id);
    } else {
      integrantesAAsignar = asignarMasivoForm.integrantes_ids;
    }

    if (integrantesAAsignar.length === 0) {
      toast.error('No hay integrantes para asignar');
      return;
    }

    let exitosos = 0;
    let errores = 0;

    for (const integranteId of integrantesAAsignar) {
      try {
        await axios.post(`${API}/clubes-deportivos/${clubId}/asignar-plan`, {
          integrante_id: integranteId,
          plan_id: asignarMasivoForm.plan_id,
          fecha_inicio: asignarMasivoForm.fecha_inicio,
          cuota_inicio: asignarMasivoForm.cuota_inicio || 1
        }, { withCredentials: true });
        exitosos++;
      } catch (error) {
        errores++;
      }
    }

    toast.success(`Plan asignado a ${exitosos} integrantes${errores > 0 ? `. ${errores} ya tenían el plan asignado.` : ''}`);
    setShowAsignarMasivoDialog(false);
    setAsignarMasivoForm({ modo: 'categoria', categoria_id: '', integrantes_ids: [], plan_id: '', fecha_inicio: new Date().toISOString().split('T')[0], cuota_inicio: 1 });
    loadData();
  };

  // Registrar pago
  const handleRegistrarPago = async () => {
    try {
      const res = await axios.post(`${API}/clubes-deportivos/${clubId}/pagos`, {
        ...pagoForm,
        monto: parseFloat(pagoForm.monto)
      }, { withCredentials: true });
      toast.success(res.data.message);
      setShowPagoDialog(false);
      setPagoForm({
        integrante_id: '', cuota_ids: [], monto: '', fecha_pago: new Date().toISOString().split('T')[0],
        glosa: '', metodo_pago: 'efectivo', comprobante: ''
      });
      setSelectedIntegrante(null);
      setIntegranteCuotas(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    }
  };

  // Crear cargo excepcional
  const handleCreateCargo = async () => {
    try {
      await axios.post(`${API}/clubes-deportivos/${clubId}/cargos-excepcionales`, {
        ...cargoForm,
        monto: parseFloat(cargoForm.monto)
      }, { withCredentials: true });
      toast.success('Cargo creado exitosamente');
      setShowCargoDialog(false);
      setCargoForm({ integrante_id: '', monto: '', descripcion: '', fecha_vencimiento: '' });
      setSelectedIntegrante(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cargo');
    }
  };

  // Crear excepción
  const handleCreateExcepcion = async () => {
    try {
      const data = {
        ...excepcionForm,
        valor: (excepcionForm.tipo === 'descuento_permanente' || excepcionForm.tipo === 'descuento_fijo') ? parseFloat(excepcionForm.valor) : null,
        meses_exencion: excepcionForm.tipo === 'exencion_temporal' ? excepcionForm.meses_exencion.split(',').map(m => m.trim()) : null
      };
      await axios.post(`${API}/clubes-deportivos/${clubId}/excepciones-pago`, data, { withCredentials: true });
      toast.success('Excepción creada exitosamente');
      setShowExcepcionDialog(false);
      setExcepcionForm({ integrante_id: '', tipo: 'descuento_permanente', valor: '', meses_exencion: '', motivo: '' });
      setSelectedIntegrante(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear excepción');
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const getEstadoBadge = (estado) => {
    const styles = {
      pendiente: 'bg-yellow-500/20 text-yellow-400',
      parcial: 'bg-blue-500/20 text-blue-400',
      pagada: 'bg-green-500/20 text-green-400',
      vencida: 'bg-red-500/20 text-red-400'
    };
    const labels = {
      pendiente: 'Pendiente',
      parcial: 'Parcial',
      pagada: 'Pagada',
      vencida: 'Vencida'
    };
    return <Badge className={styles[estado] || 'bg-slate-500'}>{labels[estado] || estado}</Badge>;
  };

  const toggleSection = (sectionName) => {
    setOpenSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  // Integrant selector component - uses local search
  const IntegranteSelector = ({ value, onChange, onClear }) => {
    const [localSearchQuery, setLocalSearchQuery] = useState('');
    const searchResults = searchIntegrantesLocal(localSearchQuery);
    
    return (
      <div>
        <Label className="text-slate-300">Buscar Integrante</Label>
        <div className="relative">
          <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre, apellido o RUT..."
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            className="pl-10 bg-slate-700 border-slate-600"
            data-testid="search-integrante-input"
          />
        </div>
        {searchResults.length > 0 && !value && (
          <div className="mt-2 bg-slate-700 rounded-lg border border-slate-600 max-h-32 overflow-y-auto">
            {searchResults.map(int => (
              <button
                key={int.id}
                className="w-full px-4 py-2 text-left hover:bg-slate-600 text-white"
                data-testid={`search-result-${int.id}`}
                onClick={() => {
                  onChange(int);
                  setLocalSearchQuery('');
                }}
              >
                {int.nombre} {int.apellido} - {int.rut}
              </button>
            ))}
          </div>
        )}
        {localSearchQuery.length >= 2 && searchResults.length === 0 && !value && (
          <p className="mt-2 text-sm text-slate-400">No se encontraron integrantes</p>
        )}
        {value && (
          <div className="mt-2 p-3 bg-slate-700 rounded-lg flex justify-between items-center">
            <span className="text-white">{value.nombre} {value.apellido}</span>
            <Button size="sm" variant="ghost" onClick={onClear} data-testid="clear-integrante-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // RESUMEN Section
  if (section === 'resumen') {
    return (
      <div className="space-y-4">
        {resumen && (
          <>
            {/* SECCIÓN 0: SALDO EN CAJA (Sin filtro) */}
            <Collapsible open={openSections.saldoCaja} onOpenChange={() => toggleSection('saldoCaja')}>
              <Card className={`border-2 ${resumen.saldo_caja >= 0 ? 'bg-sky-900/20 border-sky-500/50' : 'bg-red-900/20 border-red-500/50'}`}>
                <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-700/30 rounded-t-lg">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <DollarSign className={`w-6 h-6 ${resumen.saldo_caja >= 0 ? 'text-sky-400' : 'text-red-400'}`} />
                    Saldo en Caja (Total Histórico)
                  </h3>
                  {openSections.saldoCaja ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0">
                    <div className="text-center mb-4">
                      <p className={`text-4xl font-bold ${resumen.saldo_caja >= 0 ? 'text-sky-400' : 'text-red-400'}`}>
                        {formatMoney(resumen.saldo_caja)}
                      </p>
                      <p className="text-sm text-white mt-1">Ingresos - Egresos (Todo el historial)</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                        <p className="text-xs text-white">Total Ingresos</p>
                        <p className="text-lg font-bold text-sky-400">{formatMoney(resumen.total_ingresos_historico)}</p>
                      </div>
                      <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                        <p className="text-xs text-white">Total Egresos</p>
                        <p className="text-lg font-bold text-red-400">{formatMoney(resumen.total_egresos_historico)}</p>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* FILTROS */}
            <Card className="bg-slate-800 border-slate-700 p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-white text-xs">Desde</Label>
                  <Input
                    type="date"
                    value={filtros.fecha_desde}
                    onChange={(e) => setFiltros({...filtros, fecha_desde: e.target.value})}
                    className="bg-slate-700 border-slate-600 h-9"
                  />
                </div>
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-white text-xs">Hasta</Label>
                  <Input
                    type="date"
                    value={filtros.fecha_hasta}
                    onChange={(e) => setFiltros({...filtros, fecha_hasta: e.target.value})}
                    className="bg-slate-700 border-slate-600 h-9"
                  />
                </div>
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-white text-xs">Categoría</Label>
                  <Select value={filtros.categoria_id} onValueChange={(v) => setFiltros({...filtros, categoria_id: v})}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 h-9">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todas las categorías</SelectItem>
                      {categorias.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" onClick={loadResumen} className="border-slate-600">
                  <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
                </Button>
              </div>
            </Card>

            {/* SECCIÓN 1: SALDO DEL PERÍODO */}
            <Collapsible open={openSections.saldoPeriodo} onOpenChange={() => toggleSection('saldoPeriodo')}>
              <Card className="bg-slate-800 border-slate-700">
                <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-700/50 rounded-t-lg">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-sky-400" />
                    Saldo del Período
                  </h3>
                  {openSections.saldoPeriodo ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0">
                    <div className="text-center mb-4">
                      <p className={`text-3xl font-bold ${resumen.saldo_periodo >= 0 ? 'text-sky-400' : 'text-red-400'}`}>
                        {formatMoney(resumen.saldo_periodo)}
                      </p>
                      <p className="text-sm text-white">Período: {filtros.fecha_desde} al {filtros.fecha_hasta}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-3 bg-sky-500/10 border border-sky-500/30 rounded-lg">
                        <p className="text-xs text-white">Ingresos Período</p>
                        <p className="text-xl font-bold text-sky-400">{formatMoney(resumen.ingresos_periodo)}</p>
                        <p className="text-xs text-slate-400 mt-1">Cuotas: {formatMoney(resumen.pagos_cuotas_periodo)}</p>
                        <p className="text-xs text-slate-400">Otros: {formatMoney(resumen.otros_ingresos_periodo)}</p>
                      </div>
                      <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-xs text-white">Egresos Período</p>
                        <p className="text-xl font-bold text-red-400">{formatMoney(resumen.egresos_periodo)}</p>
                      </div>
                      <div className={`p-3 rounded-lg ${resumen.saldo_periodo >= 0 ? 'bg-sky-500/10 border border-sky-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                        <p className="text-xs text-white">Balance</p>
                        <p className={`text-xl font-bold ${resumen.saldo_periodo >= 0 ? 'text-sky-400' : 'text-red-400'}`}>
                          {formatMoney(resumen.saldo_periodo)}
                        </p>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* SECCIÓN 2: MOROSIDADES */}
            <Collapsible open={openSections.morosidades} onOpenChange={() => toggleSection('morosidades')}>
              <Card className="bg-slate-800 border-slate-700">
                <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-700/50 rounded-t-lg">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    Morosidades
                  </h3>
                  {openSections.morosidades ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-xs text-white">Total Morosidad</p>
                      <p className="text-2xl font-bold text-red-400">{formatMoney(resumen.deuda_vencida)}</p>
                      <p className="text-xs text-slate-400 mt-1">Cuotas vencidas</p>
                    </div>
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-xs text-white">Deuda Total</p>
                      <p className="text-2xl font-bold text-red-400">{formatMoney(resumen.total_deuda)}</p>
                      <p className="text-xs text-slate-400 mt-1">Pendiente + vencido</p>
                    </div>
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-xs text-white">Cuotas Impagas</p>
                      <p className="text-2xl font-bold text-red-400">{resumen.cuotas_pendientes + resumen.cuotas_vencidas}</p>
                      <p className="text-xs text-slate-400 mt-1">{resumen.cuotas_vencidas} vencidas / {resumen.cuotas_pendientes} pendientes</p>
                    </div>
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-xs text-white">Integrantes con Deuda</p>
                      <p className="text-2xl font-bold text-red-400">{resumen.integrantes_con_deuda}</p>
                      <p className="text-xs text-slate-400 mt-1">{resumen.integrantes_suspendidos} suspendidos</p>
                    </div>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* SECCIÓN 3 y 4: INGRESOS Y EGRESOS POR TIPO */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Ingresos por tipo */}
              <Collapsible open={openSections.ingresos} onOpenChange={() => toggleSection('ingresos')}>
                <Card className="bg-slate-800 border-slate-700 h-full">
                  <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-700/50 rounded-t-lg">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-sky-400" />
                      Ingresos por Tipo
                    </h3>
                    {openSections.ingresos ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="p-4 pt-0 space-y-2">
                      <div className="flex justify-between items-center p-2 bg-sky-500/10 rounded">
                        <span className="text-white">Cuotas</span>
                        <span className="text-sky-400 font-bold">{formatMoney(resumen.pagos_cuotas_periodo)}</span>
                      </div>
                      {resumen.ingresos_por_tipo && Object.entries(resumen.ingresos_por_tipo).map(([tipo, monto]) => (
                        <div key={tipo} className="flex justify-between items-center p-2 bg-slate-700/30 rounded">
                          <span className="text-white">{tipo}</span>
                          <span className="text-sky-400 font-medium">{formatMoney(monto)}</span>
                        </div>
                      ))}
                      {(!resumen.ingresos_por_tipo || Object.keys(resumen.ingresos_por_tipo).length === 0) && (
                        <p className="text-slate-400 text-sm text-center py-2">Sin otros ingresos en el período</p>
                      )}
                    </div>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Egresos por tipo */}
              <Collapsible open={openSections.egresos} onOpenChange={() => toggleSection('egresos')}>
                <Card className="bg-slate-800 border-slate-700 h-full">
                  <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-700/50 rounded-t-lg">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Receipt className="w-5 h-5 text-red-400" />
                      Egresos por Tipo
                    </h3>
                    {openSections.egresos ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="p-4 pt-0 space-y-2">
                      {resumen.egresos_por_tipo && Object.entries(resumen.egresos_por_tipo).map(([tipo, monto]) => (
                        <div key={tipo} className="flex justify-between items-center p-2 bg-slate-700/30 rounded">
                          <span className="text-white">{tipo}</span>
                          <span className="text-red-400 font-medium">{formatMoney(monto)}</span>
                        </div>
                      ))}
                      {(!resumen.egresos_por_tipo || Object.keys(resumen.egresos_por_tipo).length === 0) && (
                        <p className="text-slate-400 text-sm text-center py-2">Sin egresos en el período</p>
                      )}
                    </div>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            </div>

            {/* SECCIÓN 5: GRÁFICOS */}
            <Collapsible open={openSections.graficos} onOpenChange={() => toggleSection('graficos')}>
              <Card className="bg-slate-800 border-slate-700">
                <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-700/50 rounded-t-lg">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-sky-400" />
                    Gráficos Mensuales ({new Date().getFullYear()})
                  </h3>
                  {openSections.graficos ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0 space-y-6">
                    {/* Gráfico de barras: Ingresos vs Egresos */}
                    <div>
                      <h4 className="text-sm font-medium text-white mb-3">Ingresos vs Egresos por Mes</h4>
                      <div className="flex items-end gap-1 h-48 bg-slate-900/50 rounded-lg p-4">
                        {resumen.datos_mensuales?.map((mes, idx) => {
                          const maxVal = Math.max(...resumen.datos_mensuales.map(m => Math.max(m.ingresos, m.egresos))) || 1;
                          const ingresosH = (mes.ingresos / maxVal) * 100;
                          const egresosH = (mes.egresos / maxVal) * 100;
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                              <div className="flex gap-0.5 items-end h-32 w-full">
                                <div 
                                  className="flex-1 bg-sky-500/70 rounded-t transition-all" 
                                  style={{height: `${ingresosH}%`}}
                                  title={`Ingresos: ${formatMoney(mes.ingresos)}`}
                                />
                                <div 
                                  className="flex-1 bg-red-500/70 rounded-t transition-all" 
                                  style={{height: `${egresosH}%`}}
                                  title={`Egresos: ${formatMoney(mes.egresos)}`}
                                />
                              </div>
                              <span className="text-xs text-white">{mes.mes_nombre}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex gap-4 justify-center mt-2">
                        <span className="text-xs text-white flex items-center gap-1">
                          <div className="w-3 h-3 bg-sky-500/70 rounded"></div> Ingresos
                        </span>
                        <span className="text-xs text-white flex items-center gap-1">
                          <div className="w-3 h-3 bg-red-500/70 rounded"></div> Egresos
                        </span>
                      </div>
                    </div>

                    {/* Gráfico de línea: Saldo Acumulado */}
                    <div>
                      <h4 className="text-sm font-medium text-white mb-3">Saldo Acumulado por Mes</h4>
                      <div className="relative h-32 bg-slate-900/50 rounded-lg p-4">
                        <svg className="w-full h-full" viewBox="0 0 100 50" preserveAspectRatio="none">
                          {resumen.datos_mensuales && (() => {
                            const datos = resumen.datos_mensuales;
                            const maxVal = Math.max(...datos.map(m => Math.abs(m.saldo_acumulado))) || 1;
                            const minVal = Math.min(...datos.map(m => m.saldo_acumulado), 0);
                            const range = maxVal - minVal || 1;
                            const points = datos.map((m, i) => {
                              const x = (i / (datos.length - 1)) * 100;
                              const y = 50 - ((m.saldo_acumulado - minVal) / range) * 50;
                              return `${x},${y}`;
                            }).join(' ');
                            return (
                              <>
                                <polyline
                                  fill="none"
                                  stroke="#38bdf8"
                                  strokeWidth="2"
                                  points={points}
                                />
                                {datos.map((m, i) => {
                                  const x = (i / (datos.length - 1)) * 100;
                                  const y = 50 - ((m.saldo_acumulado - minVal) / range) * 50;
                                  return (
                                    <circle key={i} cx={x} cy={y} r="1.5" fill="#38bdf8" />
                                  );
                                })}
                              </>
                            );
                          })()}
                        </svg>
                        <div className="flex justify-between mt-1">
                          {resumen.datos_mensuales?.map((m, i) => (
                            <span key={i} className="text-xs text-white">{m.mes_nombre}</span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Gráfico: Personas que pagaron por mes */}
                    <div>
                      <h4 className="text-sm font-medium text-white mb-3">Personas que Pagaron por Mes</h4>
                      <div className="flex items-end gap-1 h-32 bg-slate-900/50 rounded-lg p-4">
                        {resumen.datos_mensuales?.map((mes, idx) => {
                          const maxPersonas = Math.max(...resumen.datos_mensuales.map(m => m.personas_pagaron)) || 1;
                          const h = (mes.personas_pagaron / maxPersonas) * 100;
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                              <span className="text-xs text-sky-400">{mes.personas_pagaron}</span>
                              <div 
                                className="w-full bg-sky-500/70 rounded-t transition-all" 
                                style={{height: `${h}%`, minHeight: mes.personas_pagaron > 0 ? '4px' : '0'}}
                              />
                              <span className="text-xs text-white">{mes.mes_nombre}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          </>
        )}

        {/* Dialogs for Resumen */}
        {renderDialogs()}
      </div>
    );
  }

  // PLANES Section
  if (section === 'planes') {
    const planesFiltrados = planes.filter(p => 
      !searchPlan || p.nombre?.toLowerCase().includes(searchPlan.toLowerCase())
    );
    
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="relative flex-1 w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar plan..."
              value={searchPlan}
              onChange={(e) => setSearchPlan(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white"
            />
          </div>
          <div className="flex items-center gap-3">
            {/* Toggle de vista */}
            <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
              <button
                onClick={() => onToggleViewMode?.('cards')}
                className={`p-1.5 rounded ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
                title="Vista de tarjetas"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => onToggleViewMode?.('list')}
                className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
                title="Vista de lista"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => onRefresh?.()}
                disabled={refreshing}
                className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-600 disabled:opacity-50"
                title="Actualizar"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
            {canEdit && (
              <Button onClick={() => { setEditingPlan(null); setPlanForm({ nombre: '', monto: '', frecuencia: 'mensual', numero_cuotas: 12, dia_vencimiento: 5, descripcion: '' }); setShowPlanDialog(true); }} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" /> Nuevo Plan
              </Button>
            )}
          </div>
        </div>

        {planesFiltrados.length === 0 && planes.length === 0 ? (
          <Card className="p-8 bg-slate-800 border-slate-700 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-500 mb-4" />
            <p className="text-slate-400">No hay planes de cuotas creados</p>
            {canEdit && (
              <Button onClick={() => setShowPlanDialog(true)} className="mt-4 bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" /> Crear Primer Plan
              </Button>
            )}
          </Card>
        ) : planesFiltrados.length === 0 ? (
          <Card className="p-8 bg-slate-800 border-slate-700 text-center">
            <Search className="w-12 h-12 mx-auto text-slate-500 mb-4" />
            <p className="text-slate-400">No se encontraron planes</p>
          </Card>
        ) : viewMode === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {planesFiltrados.map(plan => (
              <Card key={plan.id} className="p-4 bg-slate-800 border-slate-700">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-white">{plan.nombre}</h3>
                      <Badge className={plan.activo ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}>
                        {plan.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-slate-400">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    <span className="font-medium text-white">{formatMoney(plan.monto)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{plan.frecuencia} - {plan.numero_cuotas} cuotas</span>
                  </div>
                  <p className="text-xs">Vencimiento: día {plan.dia_vencimiento}</p>
                </div>
                {plan.descripcion && <p className="text-sm text-slate-500 mt-2">{plan.descripcion}</p>}
                {canEdit && (
                  <div className="mt-3 pt-3 border-t border-slate-700 flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-1 text-slate-400 hover:text-white hover:bg-slate-600"
                      onClick={() => {
                        setEditingPlan(plan);
                        setPlanForm({
                          nombre: plan.nombre,
                          monto: plan.monto.toString(),
                          frecuencia: plan.frecuencia,
                          numero_cuotas: plan.numero_cuotas,
                          dia_vencimiento: plan.dia_vencimiento,
                          descripcion: plan.descripcion || ''
                        });
                        setShowPlanDialog(true);
                      }}
                    >
                      <Pencil className="w-3 h-3 mr-1" /> Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-slate-400 hover:text-red-400 hover:bg-red-600/10"
                      onClick={() => handleDeletePlan(plan.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        ) : (
          /* Vista de lista */
          <Card className="bg-slate-800 border-slate-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Plan</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Monto</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-300 hidden md:table-cell">Frecuencia</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-300 hidden lg:table-cell">Cuotas</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Estado</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {planesFiltrados.map(plan => (
                  <tr key={plan.id} className="hover:bg-slate-700/30">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-white">{plan.nombre}</p>
                        {plan.descripcion && <p className="text-xs text-slate-500">{plan.descripcion}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white font-medium">{formatMoney(plan.monto)}</td>
                    <td className="px-4 py-3 text-slate-300 hidden md:table-cell capitalize">{plan.frecuencia}</td>
                    <td className="px-4 py-3 text-slate-300 hidden lg:table-cell">{plan.numero_cuotas}</td>
                    <td className="px-4 py-3">
                      <Badge className={plan.activo ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}>
                        {plan.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canEdit && (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-400 hover:text-white"
                            onClick={() => {
                              setEditingPlan(plan);
                              setPlanForm({
                                nombre: plan.nombre,
                                monto: plan.monto.toString(),
                                frecuencia: plan.frecuencia,
                                numero_cuotas: plan.numero_cuotas,
                                dia_vencimiento: plan.dia_vencimiento,
                                descripcion: plan.descripcion || ''
                              });
                              setShowPlanDialog(true);
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-400 hover:text-red-400"
                            onClick={() => handleDeletePlan(plan.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {renderDialogs()}
      </div>
    );
  }

  // INGRESOS Section
  if (section === 'ingresos') {
    return (
      <div className="space-y-6">
        <Card className="p-6 bg-slate-800 border-slate-700">
          <IntegranteSelector
            value={selectedIntegrante}
            onChange={(int) => {
              setSelectedIntegrante(int);
              setPagoForm(prev => ({ ...prev, integrante_id: int.id, cuota_ids: [] }));
              loadIntegranteCuotas(int.id);
            }}
            onClear={() => {
              setSelectedIntegrante(null);
              setPagoForm(prev => ({ ...prev, integrante_id: '', cuota_ids: [] }));
              setIntegranteCuotas(null);
            }}
          />

          {selectedIntegrante && integranteCuotas && (
            <div className="mt-6 space-y-4">
              <Card className="p-4 bg-slate-700 border-slate-600">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {selectedIntegrante.nombre} {selectedIntegrante.apellido}
                    </h3>
                    <p className="text-sm text-slate-400">{selectedIntegrante.rut}</p>
                    {integranteCuotas.plan_asignado && (
                      <p className="text-sm text-green-400 mt-1">
                        Plan: {integranteCuotas.plan_asignado.plan.nombre}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-400">Deuda Total</p>
                    <p className="text-xl font-bold text-red-400">{formatMoney(integranteCuotas.resumen?.total_deuda || 0)}</p>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <Label className="text-slate-300">Seleccionar Cuotas a Pagar</Label>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {integranteCuotas.cuotas?.filter(c => c.estado !== 'pagada').length === 0 ? (
                      <p className="text-slate-400 text-sm py-4 text-center">No hay cuotas pendientes</p>
                    ) : (
                      integranteCuotas.cuotas?.filter(c => c.estado !== 'pagada').map(cuota => (
                        <label key={cuota.id} className="flex items-center gap-3 p-2 bg-slate-600 rounded-lg cursor-pointer hover:bg-slate-500">
                          <input
                            type="checkbox"
                            checked={pagoForm.cuota_ids.includes(cuota.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPagoForm(prev => ({ ...prev, cuota_ids: [...prev.cuota_ids, cuota.id] }));
                              } else {
                                setPagoForm(prev => ({ ...prev, cuota_ids: prev.cuota_ids.filter(id => id !== cuota.id) }));
                              }
                            }}
                            className="rounded border-slate-500"
                          />
                          <div className="flex-1">
                            <span className="text-white">{cuota.descripcion}</span>
                            <span className="text-slate-400 text-sm ml-2">Vence: {cuota.fecha_vencimiento}</span>
                          </div>
                          <div className="text-right flex items-center gap-2">
                            <span className="text-white font-medium">{formatMoney(cuota.monto_final - cuota.monto_pagado)}</span>
                            {getEstadoBadge(cuota.estado)}
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Monto a Pagar</Label>
                    <Input
                      type="number"
                      value={pagoForm.monto}
                      onChange={(e) => setPagoForm(prev => ({ ...prev, monto: e.target.value }))}
                      placeholder="0"
                      className="bg-slate-600 border-slate-500 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Fecha de Pago</Label>
                    <Input
                      type="date"
                      value={pagoForm.fecha_pago}
                      onChange={(e) => setPagoForm(prev => ({ ...prev, fecha_pago: e.target.value }))}
                      className="bg-slate-600 border-slate-500 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Glosa / Descripción</Label>
                    <Input
                      value={pagoForm.glosa}
                      onChange={(e) => setPagoForm(prev => ({ ...prev, glosa: e.target.value }))}
                      placeholder="Ej: Pago cuota enero"
                      className="bg-slate-600 border-slate-500 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Método de Pago</Label>
                    <Select value={pagoForm.metodo_pago} onValueChange={(v) => setPagoForm(prev => ({ ...prev, metodo_pago: v }))}>
                      <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="transferencia">Transferencia</SelectItem>
                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-4">
                  <Label className="text-slate-300">Comprobante (opcional)</Label>
                  <Input
                    value={pagoForm.comprobante}
                    onChange={(e) => setPagoForm(prev => ({ ...prev, comprobante: e.target.value }))}
                    placeholder="Número de comprobante o referencia"
                    className="bg-slate-600 border-slate-500 text-white"
                  />
                </div>

                <Button
                  className="w-full mt-4 bg-green-600 hover:bg-green-700"
                  onClick={handleRegistrarPago}
                  disabled={!pagoForm.monto || pagoForm.cuota_ids.length === 0 || !pagoForm.glosa}
                >
                  <DollarSign className="w-4 h-4 mr-2" /> Registrar Pago
                </Button>
              </Card>
            </div>
          )}
        </Card>
      </div>
    );
  }

  // HISTORIAL Section
  if (section === 'historial') {
    return (
      <div className="space-y-3">
        {pagos.length === 0 ? (
          <Card className="p-8 bg-slate-800 border-slate-700 text-center">
            <Receipt className="w-12 h-12 mx-auto text-slate-500 mb-4" />
            <p className="text-slate-400">No hay pagos registrados</p>
          </Card>
        ) : (
          pagos.map(pago => (
            <Card key={pago.id} className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <DollarSign className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {pago.integrante_info?.nombre} {pago.integrante_info?.apellido}
                    </p>
                    <p className="text-sm text-slate-400">{pago.glosa}</p>
                    <p className="text-xs text-slate-500">{pago.fecha_pago} • {pago.metodo_pago}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-green-400">{formatMoney(pago.monto)}</p>
                  {pago.comprobante && <p className="text-xs text-slate-500">#{pago.comprobante}</p>}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    );
  }

  // ASIGNAR PLAN INDIVIDUAL Section
  if (section === 'asignar-individual') {
    return (
      <Card className="p-6 bg-slate-800 border-slate-700 max-w-xl">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-green-400" />
          Asignación Individual
        </h3>
        <div className="space-y-4">
          <IntegranteSelector
            value={selectedIntegrante}
            onChange={(int) => {
              setSelectedIntegrante(int);
              setAsignarForm(prev => ({ ...prev, integrante_id: int.id }));
            }}
            onClear={() => {
              setSelectedIntegrante(null);
              setAsignarForm(prev => ({ ...prev, integrante_id: '' }));
            }}
          />

          <div>
            <Label className="text-slate-300">Seleccionar Plan</Label>
            <Select value={asignarForm.plan_id} onValueChange={(v) => setAsignarForm(prev => ({ ...prev, plan_id: v }))}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Seleccionar plan..." />
              </SelectTrigger>
              <SelectContent>
                {planes.filter(p => p.activo).map(plan => (
                  <SelectItem key={plan.id} value={plan.id}>
                    {plan.nombre} - {formatMoney(plan.monto)} ({plan.frecuencia})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-slate-300">Fecha de Inicio (Primera Cuota)</Label>
            <Input
              type="date"
              value={asignarForm.fecha_inicio}
              onChange={(e) => setAsignarForm(prev => ({ ...prev, fecha_inicio: e.target.value }))}
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>

          {/* Cuota de inicio */}
          {asignarForm.plan_id && (() => {
            const planSeleccionado = planes.find(p => p.id === asignarForm.plan_id);
            if (!planSeleccionado) return null;
            const numCuotas = planSeleccionado.numero_cuotas || 12;
            const cuotasAGenerar = numCuotas - (asignarForm.cuota_inicio || 1) + 1;
            const montoTotal = cuotasAGenerar * (planSeleccionado.monto || 0);
            
            return (
              <div className="space-y-2">
                <Label className="text-slate-300">¿Desde qué cuota comienza?</Label>
                <Select 
                  value={String(asignarForm.cuota_inicio || 1)} 
                  onValueChange={(v) => setAsignarForm(prev => ({ ...prev, cuota_inicio: parseInt(v) }))}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: numCuotas }, (_, i) => i + 1).map(num => (
                      <SelectItem key={num} value={String(num)}>
                        Cuota {num} de {numCuotas}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                  <p className="text-sm text-slate-300">
                    Se generarán <span className="text-green-400 font-semibold">{cuotasAGenerar} cuotas</span> (cuota {asignarForm.cuota_inicio || 1} a {numCuotas})
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Monto total: {formatMoney(montoTotal)} ({formatMoney(planSeleccionado.monto)} x {cuotasAGenerar})
                  </p>
                </div>
              </div>
            );
          })()}

          <Button onClick={handleAsignarPlan} className="w-full" disabled={!asignarForm.integrante_id || !asignarForm.plan_id}>
            Asignar Plan
          </Button>
        </div>
      </Card>
    );
  }

  // ASIGNAR PLAN MASIVO Section
  if (section === 'asignar-masivo') {
    return (
      <Card className="p-6 bg-slate-800 border-slate-700 max-w-xl">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-400" />
          Asignación Masiva
        </h3>
        <div className="space-y-4">
          <div>
            <Label className="text-slate-300">Modo de Asignación</Label>
            <Select value={asignarMasivoForm.modo} onValueChange={(v) => { setMassSearchQuery(''); setAsignarMasivoForm(prev => ({ ...prev, modo: v, integrantes_ids: [] })); }}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="categoria">Por Categoría</SelectItem>
                <SelectItem value="seleccion">Selección Manual</SelectItem>
                <SelectItem value="todos">Todos los Integrantes</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {asignarMasivoForm.modo === 'categoria' && (
            <div>
              <Label className="text-slate-300">Seleccionar Categoría</Label>
              <Select value={asignarMasivoForm.categoria_id} onValueChange={(v) => setAsignarMasivoForm(prev => ({ ...prev, categoria_id: v }))}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Seleccionar categoría..." />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map(cat => {
                    const count = integrantesAprobados.filter(i => i.categoria_ids?.includes(cat.id)).length;
                    return (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.nombre} ({count} integrantes)
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          {asignarMasivoForm.modo === 'seleccion' && (
            <div>
              <Label className="text-slate-300">Seleccionar Integrantes ({asignarMasivoForm.integrantes_ids.length})</Label>
              <div className="relative mt-2 mb-2">
                <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Filtrar integrantes..."
                  value={massSearchQuery}
                  onChange={(e) => setMassSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-600 border-slate-500 text-white"
                  data-testid="mass-filter-input"
                />
              </div>
              {massSearchQuery.length >= 2 && asignarMasivoForm.integrantes_ids.length > 0 && (
                <p className="text-xs text-green-400 mb-2">
                  {asignarMasivoForm.integrantes_ids.length} seleccionados (el filtro no afecta la selección)
                </p>
              )}
              <div className="max-h-40 overflow-y-auto bg-slate-700 rounded-lg border border-slate-600 p-2 space-y-1">
                {filteredIntegrantesForMass.map(int => (
                  <label key={int.id} className="flex items-center gap-2 p-1 hover:bg-slate-600 rounded cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={asignarMasivoForm.integrantes_ids.includes(int.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAsignarMasivoForm(prev => ({ ...prev, integrantes_ids: [...prev.integrantes_ids, int.id] }));
                        } else {
                          setAsignarMasivoForm(prev => ({ ...prev, integrantes_ids: prev.integrantes_ids.filter(id => id !== int.id) }));
                        }
                      }}
                      className="rounded"
                      data-testid={`mass-checkbox-${int.id}`}
                    />
                    <span className="text-white">{int.nombre} {int.apellido}</span>
                  </label>
                ))}
                {filteredIntegrantesForMass.length === 0 && (
                  <p className="text-slate-400 text-sm p-2 text-center">No se encontraron integrantes</p>
                )}
              </div>
            </div>
          )}

          {asignarMasivoForm.modo === 'todos' && (
            <div className="p-3 bg-blue-500/20 rounded-lg border border-blue-500/30">
              <p className="text-blue-300 text-sm">
                Se asignará a <strong>{integrantesAprobados.length}</strong> integrantes
              </p>
            </div>
          )}

          <div>
            <Label className="text-slate-300">Seleccionar Plan</Label>
            <Select value={asignarMasivoForm.plan_id} onValueChange={(v) => setAsignarMasivoForm(prev => ({ ...prev, plan_id: v }))}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Seleccionar plan..." />
              </SelectTrigger>
              <SelectContent>
                {planes.filter(p => p.activo).map(plan => (
                  <SelectItem key={plan.id} value={plan.id}>
                    {plan.nombre} - {formatMoney(plan.monto)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-slate-300">Fecha de Inicio (Primera Cuota)</Label>
            <Input
              type="date"
              value={asignarMasivoForm.fecha_inicio}
              onChange={(e) => setAsignarMasivoForm(prev => ({ ...prev, fecha_inicio: e.target.value }))}
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>

          {/* Cuota de inicio para asignación masiva */}
          {asignarMasivoForm.plan_id && (() => {
            const planSeleccionado = planes.find(p => p.id === asignarMasivoForm.plan_id);
            if (!planSeleccionado) return null;
            const numCuotas = planSeleccionado.numero_cuotas || 12;
            const cuotasAGenerar = numCuotas - (asignarMasivoForm.cuota_inicio || 1) + 1;
            
            return (
              <div className="space-y-2">
                <Label className="text-slate-300">¿Desde qué cuota comienza?</Label>
                <Select 
                  value={String(asignarMasivoForm.cuota_inicio || 1)} 
                  onValueChange={(v) => setAsignarMasivoForm(prev => ({ ...prev, cuota_inicio: parseInt(v) }))}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: numCuotas }, (_, i) => i + 1).map(num => (
                      <SelectItem key={num} value={String(num)}>
                        Cuota {num} de {numCuotas}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-blue-300">
                  Se generarán {cuotasAGenerar} cuotas por integrante (cuota {asignarMasivoForm.cuota_inicio || 1} a {numCuotas})
                </p>
              </div>
            );
          })()}

          <Button 
            onClick={handleAsignarMasivo} 
            className="w-full bg-blue-600 hover:bg-blue-700" 
            disabled={!asignarMasivoForm.plan_id || (asignarMasivoForm.modo === 'categoria' && !asignarMasivoForm.categoria_id) || (asignarMasivoForm.modo === 'seleccion' && asignarMasivoForm.integrantes_ids.length === 0)}
          >
            Asignar Masivamente
          </Button>
        </div>
      </Card>
    );
  }

  // CARGO EXCEPCIONAL Section (legacy support)
  if (section === 'cargo') {
    return (
      <Card className="p-6 bg-slate-800 border-slate-700 max-w-xl">
        <div className="space-y-4">
          <IntegranteSelector
            value={selectedIntegrante}
            onChange={(int) => {
              setSelectedIntegrante(int);
              setCargoForm(prev => ({ ...prev, integrante_id: int.id }));
            }}
            onClear={() => {
              setSelectedIntegrante(null);
              setCargoForm(prev => ({ ...prev, integrante_id: '' }));
            }}
          />

          <div>
            <Label className="text-slate-300">Monto</Label>
            <Input
              type="number"
              value={cargoForm.monto}
              onChange={(e) => setCargoForm(prev => ({ ...prev, monto: e.target.value }))}
              placeholder="0"
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>

          <div>
            <Label className="text-slate-300">Descripción (Glosa)</Label>
            <Input
              value={cargoForm.descripcion}
              onChange={(e) => setCargoForm(prev => ({ ...prev, descripcion: e.target.value }))}
              placeholder="Ej: Equipamiento, Multa, etc."
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>

          <div>
            <Label className="text-slate-300">Fecha de Vencimiento</Label>
            <Input
              type="date"
              value={cargoForm.fecha_vencimiento}
              onChange={(e) => setCargoForm(prev => ({ ...prev, fecha_vencimiento: e.target.value }))}
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>

          <Button onClick={handleCreateCargo} className="w-full" disabled={!cargoForm.integrante_id || !cargoForm.monto || !cargoForm.descripcion || !cargoForm.fecha_vencimiento}>
            Crear Cargo
          </Button>
        </div>
      </Card>
    );
  }

  // EXCEPCIÓN Section
  if (section === 'excepcion') {
    return (
      <Card className="p-6 bg-slate-800 border-slate-700 max-w-xl">
        <div className="space-y-4">
          <IntegranteSelector
            value={selectedIntegrante}
            onChange={(int) => {
              setSelectedIntegrante(int);
              setExcepcionForm(prev => ({ ...prev, integrante_id: int.id }));
            }}
            onClear={() => {
              setSelectedIntegrante(null);
              setExcepcionForm(prev => ({ ...prev, integrante_id: '' }));
            }}
          />

          <div>
            <Label className="text-slate-300">Tipo de Excepción</Label>
            <Select value={excepcionForm.tipo} onValueChange={(v) => setExcepcionForm(prev => ({ ...prev, tipo: v }))}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="descuento_permanente">Descuento Permanente (%)</SelectItem>
                <SelectItem value="descuento_fijo">Descuento Monto Fijo ($)</SelectItem>
                <SelectItem value="exencion_total">Exención Total</SelectItem>
                <SelectItem value="exencion_temporal">Exención Temporal (meses específicos)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {excepcionForm.tipo === 'descuento_permanente' && (
            <div>
              <Label className="text-slate-300">Porcentaje de Descuento</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={excepcionForm.valor}
                onChange={(e) => setExcepcionForm(prev => ({ ...prev, valor: e.target.value }))}
                placeholder="Ej: 20"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
          )}

          {excepcionForm.tipo === 'descuento_fijo' && (
            <div>
              <Label className="text-slate-300">Monto Fijo de Descuento</Label>
              <Input
                type="number"
                min="1"
                value={excepcionForm.valor}
                onChange={(e) => setExcepcionForm(prev => ({ ...prev, valor: e.target.value }))}
                placeholder="Ej: 5000"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
          )}

          {excepcionForm.tipo === 'exencion_temporal' && (
            <div>
              <Label className="text-slate-300">Meses de Exención (separados por coma)</Label>
              <Input
                value={excepcionForm.meses_exencion}
                onChange={(e) => setExcepcionForm(prev => ({ ...prev, meses_exencion: e.target.value }))}
                placeholder="Ej: 2025-01, 2025-02, 2025-03"
                className="bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">Formato: YYYY-MM separados por coma</p>
            </div>
          )}

          <div>
            <Label className="text-slate-300">Motivo</Label>
            <Textarea
              value={excepcionForm.motivo}
              onChange={(e) => setExcepcionForm(prev => ({ ...prev, motivo: e.target.value }))}
              placeholder="Ej: Lesión deportiva, Beca, etc."
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>

          <Button onClick={handleCreateExcepcion} className="w-full" disabled={!excepcionForm.integrante_id || !excepcionForm.motivo}>
            Crear Excepción
          </Button>
        </div>
      </Card>
    );
  }

  // Render all dialogs
  function renderDialogs() {
    return (
      <>
        {/* Dialog: Crear/Editar Plan */}
        <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">{editingPlan ? 'Editar Plan' : 'Nuevo Plan de Cuotas'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Nombre del Plan</Label>
                <Input
                  value={planForm.nombre}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder="Ej: Cuota Mensual"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Monto por Cuota</Label>
                  <Input
                    type="number"
                    value={planForm.monto}
                    onChange={(e) => setPlanForm(prev => ({ ...prev, monto: e.target.value }))}
                    placeholder="0"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Frecuencia</Label>
                  <Select value={planForm.frecuencia} onValueChange={(v) => setPlanForm(prev => ({ ...prev, frecuencia: v }))}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensual">Mensual</SelectItem>
                      <SelectItem value="trimestral">Trimestral</SelectItem>
                      <SelectItem value="semestral">Semestral</SelectItem>
                      <SelectItem value="anual">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Número de Cuotas</Label>
                  <Input
                    type="number"
                    value={planForm.numero_cuotas}
                    onChange={(e) => setPlanForm(prev => ({ ...prev, numero_cuotas: parseInt(e.target.value) || 12 }))}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Día de Vencimiento</Label>
                  <Input
                    type="number"
                    min="1"
                    max="28"
                    value={planForm.dia_vencimiento}
                    onChange={(e) => setPlanForm(prev => ({ ...prev, dia_vencimiento: parseInt(e.target.value) || 5 }))}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Descripción (opcional)</Label>
                <Textarea
                  value={planForm.descripcion}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Descripción del plan..."
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowPlanDialog(false)} className="flex-1 border-slate-600">
                  Cancelar
                </Button>
                <Button onClick={handleSavePlan} className="flex-1" disabled={!planForm.nombre || !planForm.monto}>
                  {editingPlan ? 'Guardar Cambios' : 'Crear Plan'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Asignar Plan Individual */}
        <Dialog open={showAsignarPlanDialog} onOpenChange={setShowAsignarPlanDialog}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Asignar Plan a Integrante</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <IntegranteSelector
                value={selectedIntegrante}
                onChange={(int) => {
                  setSelectedIntegrante(int);
                  setAsignarForm(prev => ({ ...prev, integrante_id: int.id }));
                }}
                onClear={() => {
                  setSelectedIntegrante(null);
                  setAsignarForm(prev => ({ ...prev, integrante_id: '' }));
                }}
              />

              <div>
                <Label className="text-slate-300">Seleccionar Plan</Label>
                <Select value={asignarForm.plan_id} onValueChange={(v) => setAsignarForm(prev => ({ ...prev, plan_id: v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Seleccionar plan..." />
                  </SelectTrigger>
                  <SelectContent>
                    {planes.filter(p => p.activo).map(plan => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.nombre} - {formatMoney(plan.monto)} ({plan.frecuencia})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-300">Fecha de Inicio del Plan</Label>
                <Input
                  type="date"
                  value={asignarForm.fecha_inicio}
                  onChange={(e) => setAsignarForm(prev => ({ ...prev, fecha_inicio: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white"
                />
                <p className="text-xs text-slate-500 mt-1">Las cuotas se generarán desde esta fecha</p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => { setShowAsignarPlanDialog(false); setSelectedIntegrante(null); }} className="flex-1 border-slate-600">
                  Cancelar
                </Button>
                <Button onClick={handleAsignarPlan} className="flex-1" disabled={!asignarForm.integrante_id || !asignarForm.plan_id}>
                  Asignar Plan
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Asignar Plan Masivo */}
        <Dialog open={showAsignarMasivoDialog} onOpenChange={setShowAsignarMasivoDialog}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">Asignar Plan a Múltiples Integrantes</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Modo de Asignación</Label>
                <Select value={asignarMasivoForm.modo} onValueChange={(v) => { setMassSearchQuery(''); setAsignarMasivoForm(prev => ({ ...prev, modo: v, integrantes_ids: [] })); }}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="categoria">Por Categoría</SelectItem>
                    <SelectItem value="seleccion">Selección Manual</SelectItem>
                    <SelectItem value="todos">Todos los Integrantes</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {asignarMasivoForm.modo === 'categoria' && (
                <div>
                  <Label className="text-slate-300">Seleccionar Categoría</Label>
                  <Select value={asignarMasivoForm.categoria_id} onValueChange={(v) => setAsignarMasivoForm(prev => ({ ...prev, categoria_id: v }))}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Seleccionar categoría..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categorias.map(cat => {
                        const count = integrantesAprobados.filter(i => i.categoria_ids?.includes(cat.id)).length;
                        return (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.nombre} ({count} integrantes)
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {asignarMasivoForm.modo === 'seleccion' && (
                <div>
                  <Label className="text-slate-300">Seleccionar Integrantes ({asignarMasivoForm.integrantes_ids.length} seleccionados)</Label>
                  {/* Search filter for manual selection - doesn't affect existing selections */}
                  <div className="relative mt-2 mb-2">
                    <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Filtrar integrantes..."
                      value={massSearchQuery}
                      onChange={(e) => setMassSearchQuery(e.target.value)}
                      className="pl-10 bg-slate-600 border-slate-500"
                      data-testid="dialog-mass-filter-input"
                    />
                  </div>
                  {/* Show selected count if filtering */}
                  {massSearchQuery.length >= 2 && asignarMasivoForm.integrantes_ids.length > 0 && (
                    <p className="text-xs text-green-400 mb-2">
                      {asignarMasivoForm.integrantes_ids.length} seleccionados (el filtro no afecta la selección)
                    </p>
                  )}
                  <div className="max-h-48 overflow-y-auto bg-slate-700 rounded-lg border border-slate-600 p-2 space-y-1">
                    {filteredIntegrantesForMass.length === 0 ? (
                      <p className="text-slate-400 text-sm p-2">No se encontraron integrantes</p>
                    ) : (
                      filteredIntegrantesForMass.map(int => (
                        <label key={int.id} className="flex items-center gap-2 p-2 hover:bg-slate-600 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={asignarMasivoForm.integrantes_ids.includes(int.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAsignarMasivoForm(prev => ({ ...prev, integrantes_ids: [...prev.integrantes_ids, int.id] }));
                              } else {
                                setAsignarMasivoForm(prev => ({ ...prev, integrantes_ids: prev.integrantes_ids.filter(id => id !== int.id) }));
                              }
                            }}
                            className="rounded"
                            data-testid={`dialog-mass-checkbox-${int.id}`}
                          />
                          <span className="text-white">{int.nombre} {int.apellido}</span>
                          <span className="text-slate-400 text-sm">{int.rut}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              {asignarMasivoForm.modo === 'todos' && (
                <div className="p-4 bg-blue-500/20 rounded-lg border border-blue-500/30">
                  <p className="text-blue-300">
                    Se asignará el plan a <strong>{integrantesAprobados.length}</strong> integrantes activos
                  </p>
                </div>
              )}

              <div>
                <Label className="text-slate-300">Seleccionar Plan</Label>
                <Select value={asignarMasivoForm.plan_id} onValueChange={(v) => setAsignarMasivoForm(prev => ({ ...prev, plan_id: v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Seleccionar plan..." />
                  </SelectTrigger>
                  <SelectContent>
                    {planes.filter(p => p.activo).map(plan => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.nombre} - {formatMoney(plan.monto)} ({plan.frecuencia})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-300">Fecha de Inicio del Plan</Label>
                <Input
                  type="date"
                  value={asignarMasivoForm.fecha_inicio}
                  onChange={(e) => setAsignarMasivoForm(prev => ({ ...prev, fecha_inicio: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowAsignarMasivoDialog(false)} className="flex-1 border-slate-600">
                  Cancelar
                </Button>
                <Button 
                  onClick={handleAsignarMasivo} 
                  className="flex-1 bg-blue-600 hover:bg-blue-700" 
                  disabled={!asignarMasivoForm.plan_id || (asignarMasivoForm.modo === 'categoria' && !asignarMasivoForm.categoria_id) || (asignarMasivoForm.modo === 'seleccion' && asignarMasivoForm.integrantes_ids.length === 0)}
                >
                  Asignar Plan Masivamente
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Cargo Excepcional */}
        <Dialog open={showCargoDialog} onOpenChange={setShowCargoDialog}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Crear Cargo Excepcional</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <IntegranteSelector
                value={selectedIntegrante}
                onChange={(int) => {
                  setSelectedIntegrante(int);
                  setCargoForm(prev => ({ ...prev, integrante_id: int.id }));
                }}
                onClear={() => {
                  setSelectedIntegrante(null);
                  setCargoForm(prev => ({ ...prev, integrante_id: '' }));
                }}
              />

              <div>
                <Label className="text-slate-300">Monto</Label>
                <Input
                  type="number"
                  value={cargoForm.monto}
                  onChange={(e) => setCargoForm(prev => ({ ...prev, monto: e.target.value }))}
                  placeholder="0"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              <div>
                <Label className="text-slate-300">Descripción (Glosa)</Label>
                <Input
                  value={cargoForm.descripcion}
                  onChange={(e) => setCargoForm(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Ej: Equipamiento, Multa, etc."
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              <div>
                <Label className="text-slate-300">Fecha de Vencimiento</Label>
                <Input
                  type="date"
                  value={cargoForm.fecha_vencimiento}
                  onChange={(e) => setCargoForm(prev => ({ ...prev, fecha_vencimiento: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => { setShowCargoDialog(false); setSelectedIntegrante(null); }} className="flex-1 border-slate-600">
                  Cancelar
                </Button>
                <Button onClick={handleCreateCargo} className="flex-1" disabled={!cargoForm.integrante_id || !cargoForm.monto || !cargoForm.descripcion}>
                  Crear Cargo
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Crear Excepción */}
        <Dialog open={showExcepcionDialog} onOpenChange={setShowExcepcionDialog}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Crear Excepción de Pago</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <IntegranteSelector
                value={selectedIntegrante}
                onChange={(int) => {
                  setSelectedIntegrante(int);
                  setExcepcionForm(prev => ({ ...prev, integrante_id: int.id }));
                }}
                onClear={() => {
                  setSelectedIntegrante(null);
                  setExcepcionForm(prev => ({ ...prev, integrante_id: '' }));
                }}
              />

              <div>
                <Label className="text-slate-300">Tipo de Excepción</Label>
                <Select value={excepcionForm.tipo} onValueChange={(v) => setExcepcionForm(prev => ({ ...prev, tipo: v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="descuento_permanente">Descuento Permanente (%)</SelectItem>
                    <SelectItem value="descuento_fijo">Descuento Monto Fijo ($)</SelectItem>
                    <SelectItem value="exencion_total">Exención Total</SelectItem>
                    <SelectItem value="exencion_temporal">Exención Temporal (meses específicos)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {excepcionForm.tipo === 'descuento_permanente' && (
                <div>
                  <Label className="text-slate-300">Porcentaje de Descuento</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={excepcionForm.valor}
                    onChange={(e) => setExcepcionForm(prev => ({ ...prev, valor: e.target.value }))}
                    placeholder="Ej: 20"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              )}

              {excepcionForm.tipo === 'descuento_fijo' && (
                <div>
                  <Label className="text-slate-300">Monto Fijo de Descuento</Label>
                  <Input
                    type="number"
                    min="1"
                    value={excepcionForm.valor}
                    onChange={(e) => setExcepcionForm(prev => ({ ...prev, valor: e.target.value }))}
                    placeholder="Ej: 5000"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              )}

              {excepcionForm.tipo === 'exencion_temporal' && (
                <div>
                  <Label className="text-slate-300">Meses de Exención (separados por coma)</Label>
                  <Input
                    value={excepcionForm.meses_exencion}
                    onChange={(e) => setExcepcionForm(prev => ({ ...prev, meses_exencion: e.target.value }))}
                    placeholder="Ej: 2025-01, 2025-02, 2025-03"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">Formato: YYYY-MM separados por coma</p>
                </div>
              )}

              <div>
                <Label className="text-slate-300">Motivo</Label>
                <Textarea
                  value={excepcionForm.motivo}
                  onChange={(e) => setExcepcionForm(prev => ({ ...prev, motivo: e.target.value }))}
                  placeholder="Ej: Lesión deportiva, Beca, etc."
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => { setShowExcepcionDialog(false); setSelectedIntegrante(null); }} className="flex-1 border-slate-600">
                  Cancelar
                </Button>
                <Button onClick={handleCreateExcepcion} className="flex-1" disabled={!excepcionForm.integrante_id || !excepcionForm.motivo}>
                  Crear Excepción
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Registrar Pago (desde acciones rápidas) */}
        <Dialog open={showPagoDialog} onOpenChange={(open) => { setShowPagoDialog(open); if (!open) { setSelectedIntegrante(null); setIntegranteCuotas(null); setPagoForm(prev => ({ ...prev, cuota_ids: [] })); } }}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Registrar Pago</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <IntegranteSelector
                value={selectedIntegrante}
                onChange={(int) => {
                  setSelectedIntegrante(int);
                  setPagoForm(prev => ({ ...prev, integrante_id: int.id, cuota_ids: [] }));
                  loadIntegranteCuotas(int.id);
                }}
                onClear={() => {
                  setSelectedIntegrante(null);
                  setPagoForm(prev => ({ ...prev, integrante_id: '', cuota_ids: [] }));
                  setIntegranteCuotas(null);
                }}
              />

              {selectedIntegrante && integranteCuotas && (
                <>
                  <Card className="p-3 bg-slate-700 border-slate-600">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-white font-medium">{selectedIntegrante.nombre} {selectedIntegrante.apellido}</span>
                        <span className="text-slate-400 ml-2">{selectedIntegrante.rut}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-slate-400 text-sm">Deuda: </span>
                        <span className="text-red-400 font-bold">{formatMoney(integranteCuotas.resumen?.total_deuda || 0)}</span>
                      </div>
                    </div>
                  </Card>

                  <div>
                    <Label className="text-slate-300">Seleccionar Cuotas</Label>
                    <div className="max-h-40 overflow-y-auto space-y-2 mt-2">
                      {integranteCuotas.cuotas?.filter(c => c.estado !== 'pagada').length === 0 ? (
                        <p className="text-slate-400 text-sm">No hay cuotas pendientes</p>
                      ) : (
                        integranteCuotas.cuotas?.filter(c => c.estado !== 'pagada').map(cuota => (
                          <label key={cuota.id} className="flex items-center gap-3 p-2 bg-slate-600 rounded cursor-pointer hover:bg-slate-500">
                            <input
                              type="checkbox"
                              checked={pagoForm.cuota_ids.includes(cuota.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setPagoForm(prev => ({ ...prev, cuota_ids: [...prev.cuota_ids, cuota.id] }));
                                } else {
                                  setPagoForm(prev => ({ ...prev, cuota_ids: prev.cuota_ids.filter(id => id !== cuota.id) }));
                                }
                              }}
                              className="rounded"
                            />
                            <span className="flex-1 text-white text-sm">{cuota.descripcion}</span>
                            <span className="text-slate-400 text-xs">{cuota.fecha_vencimiento}</span>
                            <span className="text-white font-medium">{formatMoney(cuota.monto_final - cuota.monto_pagado)}</span>
                            {getEstadoBadge(cuota.estado)}
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Monto</Label>
                      <Input
                        type="number"
                        value={pagoForm.monto}
                        onChange={(e) => setPagoForm(prev => ({ ...prev, monto: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Fecha</Label>
                      <Input
                        type="date"
                        value={pagoForm.fecha_pago}
                        onChange={(e) => setPagoForm(prev => ({ ...prev, fecha_pago: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-300">Glosa</Label>
                    <Input
                      value={pagoForm.glosa}
                      onChange={(e) => setPagoForm(prev => ({ ...prev, glosa: e.target.value }))}
                      placeholder="Descripción del pago"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Método</Label>
                      <Select value={pagoForm.metodo_pago} onValueChange={(v) => setPagoForm(prev => ({ ...prev, metodo_pago: v }))}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="efectivo">Efectivo</SelectItem>
                          <SelectItem value="transferencia">Transferencia</SelectItem>
                          <SelectItem value="tarjeta">Tarjeta</SelectItem>
                          <SelectItem value="otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Comprobante</Label>
                      <Input
                        value={pagoForm.comprobante}
                        onChange={(e) => setPagoForm(prev => ({ ...prev, comprobante: e.target.value }))}
                        placeholder="Opcional"
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowPagoDialog(false)} className="flex-1 border-slate-600">
                  Cancelar
                </Button>
                <Button
                  onClick={handleRegistrarPago}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={!pagoForm.integrante_id || !pagoForm.monto || pagoForm.cuota_ids.length === 0 || !pagoForm.glosa}
                >
                  Registrar Pago
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return null;
}
